import { PureComponent, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import { appointmentDraftActions, appointmentDraftSelectors, dateSlotsApi } from '~/api';
import { Calendar, loadingSpinnerActions, Messages, errorTypes, infoTypes } from '~/components';
import { analyticsListeners } from '~/analytics';

export class CalendarContainer extends PureComponent {
  constructor(props) {
    super(props);

    const { selectedDate } = props;

    this.state = {
      selectedMonth: selectedDate
        ? dayjs(selectedDate).format('YYYY-MM')
        : dayjs().format('YYYY-MM'),
      months: {},
    };
  }

  componentDidMount() {
    const { selectedMonth } = this.state;
    const { selectedDate, dispatchSetDateSlot, dispatchCountedApiCallFinished } = this.props;

    if (selectedDate) {
      this.loadMonth(dayjs(selectedMonth), true)
        .then((dates) => {
          if (!dates.length) dispatchSetDateSlot('');
        })
        .then(() => dispatchCountedApiCallFinished(true))
        .then(() => this.loadPrevAndNextMonths(selectedMonth));
    } else {
      this.loadInitialSlots(dayjs(selectedMonth)).then((dates) => {
        if (!dates.length) dispatchSetDateSlot('');
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { shopId, partnerId, dispatchSetDateSlot, dispatchCountedApiCallFinished } = this.props;
    const { shopId: prevShopId, partnerId: prevPartnerId } = prevProps;
    if (shopId !== prevShopId || partnerId !== prevPartnerId) {
      const currentDate = dayjs();
      this.resetState(currentDate);
      this.loadMonth(currentDate, true)
        .then((dates) => {
          if (!dates.length) dispatchSetDateSlot('');
        })
        .then(() => dispatchCountedApiCallFinished(true))
        .then(() => this.loadPrevAndNextMonths(currentDate.format('YYYY-MM')));
    }
  }

  loadPrevAndNextMonths = (date) => {
    const { lastCalendarMonth } = this.props;
    const { months } = this.state;

    const prevMonthStartDate = dayjs(date).subtract(1, 'month').startOf('month');
    const nextMonthStartDate = dayjs(date).add(1, 'month').startOf('month');

    const shouldLoadPreviousMonth =
      dayjs().format('YYYY-MM') < date && !months[prevMonthStartDate.format('YYYY-MM')]?.dates;
    const shouldLoadNextMonth =
      lastCalendarMonth > date && !months[nextMonthStartDate.format('YYYY-MM')]?.dates;

    if (shouldLoadPreviousMonth) {
      this.loadMonth(prevMonthStartDate, false);
    }

    if (shouldLoadNextMonth) {
      this.loadMonth(nextMonthStartDate, false);
    }
  };

  resetState = (selectedDate) => {
    this.setState({ months: {}, selectedMonth: selectedDate.format('YYYY-MM') });
  };

  loadInitialSlots = async (startDate) => {
    const { dispatchCountedApiCallFinished } = this.props;
    dispatchCountedApiCallFinished(false);
    const nextDate = startDate.add(1, 'month');
    const monthPromises = [startDate, nextDate].map((date) => this.loadMonth(date));
    const startDatePromiseResult = await monthPromises[0];
    if (!startDatePromiseResult.length) {
      const nextDatePromiseResult = await monthPromises[1];
      if (nextDatePromiseResult.length) {
        this.setState({ selectedMonth: nextDate.format('YYYY-MM') });
      }
      dispatchCountedApiCallFinished(true);
      return nextDatePromiseResult;
    }
    dispatchCountedApiCallFinished(true);
    return startDatePromiseResult;
  };

  loadMonth = (startDate, withSpinner) => {
    const { dispatchCountedApiCallFinished } = this.props;
    const month = startDate.format('YYYY-MM');
    if (withSpinner) {
      dispatchCountedApiCallFinished(false);
    }
    const promise = this.loadDates(startDate).then((dates) => {
      this.setState((state) => ({
        months: { ...state.months, [month]: { loading: false, dates } },
      }));
      return dates;
    });
    this.setState((state) => ({
      months: { ...state.months, [month]: { loading: true, promise } },
    }));
    return promise;
  };

  loadDates = (startDate) => {
    const { shopId, appointmentTypeIds, partnerId, partnersGroupId } = this.props;
    return dateSlotsApi
      .getDateSlots(
        shopId,
        appointmentTypeIds,
        startDate.format('YYYY-MM-DD'),
        startDate.endOf('month').format('YYYY-MM-DD'),
        partnerId || null,
        partnersGroupId || null,
      )
      .then((response) => response.slots)
      .catch(() => []);
  };

  onMonthChange = (month) => {
    const { dispatchCountedApiCallFinished } = this.props;
    const { months } = this.state;

    this.setState({ selectedMonth: month });
    if (!months[month]) {
      this.loadMonth(dayjs(month), true).then(() => dispatchCountedApiCallFinished(true));
    } else if (months[month]?.loading) {
      dispatchCountedApiCallFinished(false);
      months[month].promise?.then(() => dispatchCountedApiCallFinished(true));
    }
    this.loadPrevAndNextMonths(month);
  };

  onDayClick = (date) => {
    const { dispatchSetDateSlot, isEditMode } = this.props;
    dispatchSetDateSlot(date);

    if (isEditMode) {
      analyticsListeners.triggerEditPageChangesMadeAnalytics(
        'appointments',
        'appointments:date/time',
      );
    }
  };

  render() {
    const { selectedDate, lastCalendarMonth } = this.props;
    const { selectedMonth, months } = this.state;

    const displayPreviousButton = dayjs().format('YYYY-MM') < selectedMonth;
    const displayNextButton = lastCalendarMonth > selectedMonth;

    return (
      <Fragment>
        <Messages forTypes={[errorTypes.SLOT_DATE_NOT_SELECTED_ERROR]} />
        <Calendar
          dates={months[selectedMonth]?.dates ?? []}
          selectedDate={selectedDate}
          selectedMonth={selectedMonth}
          onPreviousClick={displayPreviousButton ? this.onMonthChange : null}
          onNextClick={displayNextButton ? this.onMonthChange : null}
          onDayClick={this.onDayClick}
          data-testid="Calendar"
        />
        <Messages
          forTypes={[infoTypes.NO_AVAILABLE_TIME_SLOTS, errorTypes.SLOT_TIME_UNAVAILABLE_ERROR]}
          classnameModifier="indented"
        />
      </Fragment>
    );
  }
}

export const mapStateToProps = (state) => ({
  selectedDate: appointmentDraftSelectors.selectAppointmentDateSlot(state),
  shopId: appointmentDraftSelectors.selectShop(state).id,
  partnerId: appointmentDraftSelectors.selectPartnerId(state),
  lastCalendarMonth: dayjs()
    .add(appointmentDraftSelectors.selectMinimalMaxAdvance(state), 'ms')
    .format('YYYY-MM'),
  partnersGroupId: appointmentDraftSelectors.selectPartnersGroupId(state),
  appointmentTypeIds: appointmentDraftSelectors.selectAllAppointmentTypeIds(state),
  isEditMode: state.editAppointment.editMode,
});

const mapDispatchToProps = {
  dispatchSetDateSlot: appointmentDraftActions.setDateSlot,
  dispatchCountedApiCallFinished: loadingSpinnerActions.countedApiCallFinished,
};

export const ConnectedCalendarContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(CalendarContainer);

CalendarContainer.propTypes = {
  selectedDate: PropTypes.string,
  shopId: PropTypes.string.isRequired,
  partnerId: PropTypes.string,
  partnersGroupId: PropTypes.string,
  lastCalendarMonth: PropTypes.string.isRequired,
  appointmentTypeIds: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  isEditMode: PropTypes.bool,

  dispatchSetDateSlot: PropTypes.func.isRequired,
  dispatchCountedApiCallFinished: PropTypes.func.isRequired,
};

CalendarContainer.defaultProps = {
  selectedDate: undefined,
  partnerId: undefined,
  partnersGroupId: undefined,
  isEditMode: false,
};
